import React from "react";
import { Link } from "react-router-dom";
import { CLOSE_REASONS } from "../rounds/LoomSheet";
import { useKT, T } from "../i18n/kt";
import { kp } from "../karkhanaPaths";

export type Tone = "g" | "o" | "r" | "k" | "idle";

/** The phone's date as yyyy-mm-dd (the studio's day). */
export const localDay = (d: Date = new Date()): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00`) - Date.parse(`${from}T00:00:00`)) / 86400000);

/**
 * A loom's colour on the board: green = work saved today, orange = 1–3 days without an update, red = 4 or more, grey = closed (a closure counts as an update, so it never goes red).
 */
export const loomTone = (loom: any, today: string): { tone: Tone; days: number | null } => {
  if (loom?.status === "CLOSED") return { tone: "k", days: null };
  if (loom?.status === "IDLE" || !loom?.sareeNumber) return { tone: "idle", days: null };
  const last = loom.lastEntry || loom.startedOn;
  if (!last) return { tone: "idle", days: null };
  const days = Math.max(0, daysBetween(last, today));
  return { tone: days === 0 ? "g" : days <= 3 ? "o" : "r", days };
};

const when = (t: T, days: number | null): string =>
  days === null ? "" : days === 0 ? t("today") : days === 1 ? t("yesterday") : t("{{n}} days", { n: days });

const reasonLabel = (k?: string) => CLOSE_REASONS.find((r) => r.key === k)?.label || k || "";

export const countTones = (looms: any[], today: string) => {
  const c: Record<Tone, number> = { g: 0, o: 0, r: 0, k: 0, idle: 0 };
  looms.forEach((l) => {
    c[loomTone(l, today).tone] += 1;
  });
  return c;
};

/** Every loom as a tile; tap one to enter its work or start its saree. */
const LoomBoard = ({ looms, today, onAdd }: { looms: any[]; today: string; onAdd?: () => void }) => {
  const { t } = useKT();
  const c = countTones(looms, today);
  return (
    <section className="kb" aria-label={t("Looms")}>
      <div className="kb__head">
        <h3>
          {t("Looms")} · {looms.length}
        </h3>
        <span className="kb__hint">{t("Tap a loom")}</span>
      </div>
      <div className="kb__legend">
        <span>
          <i className="kb-dot g" />
          {t("Today")} {c.g}
        </span>
        <span>
          <i className="kb-dot o" />
          {t("1–3 days")} {c.o}
        </span>
        <span>
          <i className="kb-dot r" />
          {t("4+ days")} {c.r}
        </span>
        <span>
          <i className="kb-dot k" />
          {t("Closed")} {c.k}
        </span>
        {c.idle ? (
          <span>
            <i className="kb-dot idle" />
            {t("No saree")} {c.idle}
          </span>
        ) : null}
      </div>
      <div className="kb__grid">
        {looms.map((l) => {
          const { tone, days } = loomTone(l, today);
          const label =
            tone === "k"
              ? t("Closed")
              : tone === "idle"
              ? t("No saree")
              : `#${l.sareeNumber} · ${when(t, days)}`;
          const who = tone === "k" ? t(reasonLabel(l.closedReason)) : l.weaver ? `${l.weaver.code || ""} ${l.weaver.name || ""}`.trim() : tone === "idle" ? t("Start saree") : "";
          return (
            <Link key={l.id} to={kp.rounds({ loom: l.id })} className={`kb-tile is-${tone}`} aria-label={`${t("Loom")} ${l.code}: ${label}`}>
              <span className="kb-tile__n">{l.code}</span>
              <span className="kb-tile__s">{label}</span>
              <span className="kb-tile__w">{who}</span>
            </Link>
          );
        })}
        {/* More looms can come any time, not only on the first day. */}
        {onAdd ? (
          <button type="button" className="kb-tile is-idle kh-start__add" onClick={onAdd}>
            + {t("Add looms")}
          </button>
        ) : null}
      </div>
    </section>
  );
};

export default LoomBoard;
